import React, { useEffect } from 'react';
import { Form, Input, Select, Button, Switch, Space, Card, Row, Col } from 'antd';
import { SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { TermsAndConditionsDTO, TermsAndConditionsType, ContentFormat } from '../types';
import FileUpload from '@/components/common/FileUpload';
import { useTheme } from '@/components/common/ThemeProvider';

interface TermsAndConditionsFormProps {
  initialValues?: TermsAndConditionsDTO;
  onSubmit: (values: TermsAndConditionsDTO) => void;
  onCancel: () => void;
  loading?: boolean;
}

export const TermsAndConditionsForm: React.FC<TermsAndConditionsFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const [form] = Form.useForm<TermsAndConditionsDTO>();
  const isEditing = !!initialValues?.id;
  const { dark : darkMode } = useTheme();
  
  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    }
  }, [initialValues, form]);

  const handleSubmit = (values: TermsAndConditionsDTO) => {
    onSubmit({
      ...values,
      id: initialValues?.id,
    });
  };

  const contentFormatOptions = Object.values(ContentFormat).map(format => ({
    value: format,
    label: format.replace(/_/g, ' ')
  }));

  return (
    <Card title={isEditing ? "Edit Terms & Conditions" : "Create Terms & Conditions"} className="mb-4">
      <Form 
        form={form} 
        layout="vertical" 
        onFinish={handleSubmit}
        initialValues={{ 
          active: true,
          contentFormat: ContentFormat.HTML, // Default to HTML format
        }}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              name="title" 
              label="Title" 
              rules={[{ required: true, message: 'Please enter a title' }]}
            >
              <Input placeholder="Enter title" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name="version" 
              label="Version" 
              rules={[{ required: true, message: 'Please enter version' }]}
            >
              <Input placeholder="e.g., 1.0.0" />
            </Form.Item>
          </Col>
        </Row>
        
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item 
              name="type" 
              label="Type" 
              rules={[{ required: true, message: 'Please select a type' }]}
            >
              <Select 
                placeholder="Select type"
                options={Object.values(TermsAndConditionsType).map(type => ({
                  value: type,
                  label: type.replace(/_/g, ' ')
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name="entityId" 
              label="Entity ID" 
              tooltip="Optional: Specific entity this T&C applies to (e.g., loan product ID)"
            >
              <Input placeholder="Enter entity ID (optional)" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item 
          name="contentFormat" 
          label="Content Format" 
          rules={[{ required: true, message: 'Please select a content format' }]}
        >
          <Select 
            placeholder="Select content format"
            options={contentFormatOptions}
          />
        </Form.Item>
        
        <Form.Item 
          name="content" 
          label="Content" 
          rules={[{ required: true, message: 'Please enter content' }]}
        >
          <Input.TextArea 
            rows={12} 
            placeholder="Enter terms and conditions content" 
          />
        </Form.Item>

        <Form.Item label="Document Upload (Optional)">
          <FileUpload 
            label="Terms Document" 
            name="termsDocument" 
            form={form}
            dark={darkMode}
            accept=".pdf,.doc,.docx"
            onFileUploaded={(fileId) => {
              form.setFieldsValue({ fileId });
            }}
          />
          <Form.Item 
            name="fileId" 
            hidden
            noStyle
          >
            <Input />
          </Form.Item>
        </Form.Item>
        
        <Form.Item 
          name="description" 
          label="Description (Optional)" 
        >
          <Input.TextArea 
            rows={3} 
            placeholder="Enter a brief description" 
          />
        </Form.Item>
        
        <Form.Item 
          name="active" 
          label="Active" 
          valuePropName="checked" 
          tooltip="When activated, this will deactivate other T&Cs of the same type"
        >
          <Switch />
        </Form.Item>
        
        <div className="flex justify-end">
          <Space>
            <Button 
              onClick={onCancel}
              icon={<CloseOutlined />}
            >
              Cancel
            </Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={loading}
              icon={<SaveOutlined />}
            >
              {isEditing ? 'Update' : 'Create'}
            </Button>
          </Space>
        </div>
      </Form>
    </Card>
  );
};
